import { useEffect, useRef, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { api, type Realtime as RT } from "../api";
import { useProjects } from "../project-context";

function flag(cc: string | null): string {
  if (!cc || cc.length !== 2 || cc === "??") return "";
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}
function ago(ts: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}t`;
}

export function Realtime() {
  const { selected, loading: projLoading } = useProjects();
  const [data, setData] = useState<RT | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!selected) return;
    const load = () => api.realtime(selected.id).then(setData).catch(() => {});
    load();
    timer.current = setInterval(load, 7000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [selected]);

  if (projLoading) return <div className="spin" />;
  if (!selected) {
    return <div className="card reveal" style={{ marginTop: 40 }}><h3 className="card-title">Ingen prosjekter</h3></div>;
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">{selected.name}</span>
          <h1 className="page-title">Sanntid</h1>
        </div>
        <span className="row" style={{ gap: 8, color: "var(--muted)", fontSize: 13 }}>
          <span className="status-dot up" style={{ boxShadow: "0 0 8px var(--lime)" }} /> live · oppdateres hvert 7. sek
        </span>
      </div>

      <div className="grid grid-2" style={{ marginBottom: 16 }}>
        <div className="card stat reveal">
          <div className="eyebrow">Besøkende nå</div>
          <div className="value" style={{ color: "var(--lime)" }}>{data?.visitors ?? 0}</div>
          <div className="delta">unike siste 5 min</div>
        </div>
        <div className="card reveal">
          <h3 className="card-title">Sidevisninger / min (siste 30 min)</h3>
          <ResponsiveContainer width="100%" height={90}>
            <BarChart data={data?.perMinute ?? []} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <XAxis dataKey="t" hide />
              <Tooltip
                contentStyle={{ background: "var(--panel)", border: "1px solid var(--line-strong)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12, color: "var(--text)" }}
                labelFormatter={(v) => new Date(v as string).toLocaleTimeString("nb-NO")}
              />
              <Bar dataKey="pageviews" fill="#45b793" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="card reveal">
          <h3 className="card-title">Siste hendelser</h3>
          {(data?.recent?.length ?? 0) === 0 ? (
            <div className="empty">ingen aktivitet ennå</div>
          ) : (
            <div className="bars">
              {data!.recent.map((e, i) => (
                <div className="bar-row" key={i}>
                  <span className="label">
                    <span className="flag">{flag(e.country)}</span>
                    {e.type === "track" ? (
                      <span className="mono" style={{ color: "var(--blue)" }}>⚑ {e.name}</span>
                    ) : (
                      e.path ?? "/"
                    )}
                  </span>
                  <span className="num">{ago(e.ts)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card reveal">
          <h3 className="card-title">Aktive sider (5 min)</h3>
          {(data?.topPaths?.length ?? 0) === 0 ? (
            <div className="empty">ingen sider akkurat nå</div>
          ) : (
            <div className="bars">
              {data!.topPaths.map((p, i) => {
                const max = Math.max(1, ...data!.topPaths.map((x) => x.views));
                return (
                  <div className="bar-row" key={i}>
                    <span className="fill" style={{ width: `${(p.views / max) * 100}%` }} />
                    <span className="label">{p.path}</span>
                    <span className="num">{p.views}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
